import Link from '@/routing/AppLink';
import { useRouter } from '@/routing/useAppRouter';
import { useTranslation } from 'next-i18next/pages';
import { useEffect, useRef, useState } from 'react';

import useAppsHeaderOptionData from '@/hooks/apps/useAppsHeaderOptionData';
import { useHeader } from '@/hooks/apps/useHeader';

import { NAV_MENU_ITEMS } from '@/data/shared/navMenu';

import theme from '@/styles/theme';

import LogoMobileBlack from '../../ui/header/LogoMobileBlack';
import LogoMobileWhite from '../../ui/header/LogoMobileWhite';
import MenuButton from '../../ui/header/MenuButton';
import {
  IconChevronDownRound,
  IconChevronUpRound,
  IconFacebook,
  IconLinkedIn,
  IconYoutube,
} from '../../ui/icon';
import SelectLanguage from '../../ui/selectLanguage/SelectLanguage';
import Area from '../shared/Area';
import { HeaderSubType } from '../shared/HeaderSub';
import { NavMenuAnchor } from '../shared/NavMenuAnchor';
import * as s from './Header.styles';

export type HeaderVariantType = 'transparent' | 'default';

type Props = {
  hideMenu?: boolean;
  headerSubComponent?: HeaderSubType;
};

export default function Header({ hideMenu, headerSubComponent }: Props) {
  const headerContainerRef = useRef<HTMLElement>(null);

  const { isHeaderChanges } = useAppsHeaderOptionData();

  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation('common');

  const { isFixed, isVisible, variantStyle } = useHeader({
    isHeaderChanges,
  });

  const router = useRouter();

  const menuItems = NAV_MENU_ITEMS;

  const activePath = (pathname: string) => {
    if (pathname === '/') {
      return router.pathname === pathname;
    } else {
      return (
        router.pathname.startsWith(pathname) ||
        router.asPath.startsWith(pathname)
      );
    }
  };

  const initialSelectedIndex = () => {
    const index = menuItems.findIndex((menu) =>
      menu.depth2?.some((subMenu) => activePath(subMenu.url)),
    );
    return index === -1 ? undefined : index;
  };

  const [selectedIdx, setSelectedIdx] = useState<number | undefined>(
    initialSelectedIndex,
  );

  const toggleSubmenu = (index: number) =>
    setSelectedIdx((prev) => (prev === index ? undefined : index));

  useEffect(() => {
    // 페이지 이동 시 스크롤 위치 초기화
    const handleRouteChange = () => {
      setIsOpen(false);
      window.scrollTo(0, 0);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  // isOpen 상태라면 body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    // 클린업 함수로 원래 상태 복구처리
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const renderLogo = () =>
    !isHeaderChanges || isFixed ? <LogoMobileBlack /> : <LogoMobileWhite />;

  return (
    <header
      ref={headerContainerRef}
      className={s.headerContainer(isVisible, variantStyle)}
    >
      {!headerSubComponent && (
        <>
          <Area>
            <div className={s.headerTop}>
              <Link href="/" aria-label={t('디플리 홈')} prefetch={false}>
                {renderLogo()}
              </Link>
              {!hideMenu && (
                <MenuButton
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  variant={variantStyle}
                />
              )}
            </div>
          </Area>

          <aside className={s.asideContainer(isOpen)}>
            <div className={s.selectLanguageWrapper}>
              <SelectLanguage />
            </div>

            <nav className={s.navContainer} aria-label={t('주 메뉴')}>
              <ul className={s.navList}>
                {menuItems.map((menu, index) => {
                  const submenu = menu.depth2;
                  const isActive = activePath(menu.depth1.url);

                  if (!submenu) {
                    return (
                      <li key={menu.depth1.url} className={s.depth1Container}>
                        <NavMenuAnchor
                          link={menu.depth1}
                          className={s.depth1Item(isActive)}
                          markSize={20}
                        />
                      </li>
                    );
                  }

                  const isExpanded = selectedIdx === index;
                  const depth2Id = `mobile-nav-depth2-${index}`;

                  return (
                    <li key={menu.depth1.url} className={s.depth1Container}>
                      <button
                        type="button"
                        className={s.depth1Item(isActive)}
                        onClick={() => toggleSubmenu(index)}
                        aria-expanded={isExpanded}
                        aria-controls={depth2Id}
                      >
                        {t(menu.depth1.label)}
                        <div aria-hidden="true">
                          {isExpanded ? (
                            <IconChevronUpRound
                              size={16}
                              color={theme`colors.apps.coreGrayBlack`}
                            />
                          ) : (
                            <IconChevronDownRound
                              size={16}
                              color={theme`colors.apps.coreGrayBlack`}
                            />
                          )}
                        </div>
                      </button>
                      <ul
                        id={depth2Id}
                        aria-hidden={!isExpanded}
                        inert={!isExpanded}
                        className={s.depth2Container(isExpanded)}
                      >
                        {submenu.map((subMenu) => (
                          <li key={subMenu.url}>
                            <NavMenuAnchor
                              link={subMenu}
                              className={s.depth2Item(activePath(subMenu.url))}
                            />
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className={s.socialLinksContainer}>
              <div className={s.socialLinks}>
                <a
                  href="https://www.linkedin.com/company/deeplyinc"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('LinkedIn에서 디플리 보기')}
                  className={s.socialLink}
                >
                  <IconLinkedIn size={28} aria-hidden="true" />
                </a>
                <a
                  href="https://www.facebook.com/deeplyinc"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('Facebook에서 디플리 보기')}
                  className={s.socialLink}
                >
                  <IconFacebook size={28} aria-hidden="true" />
                </a>
                <a
                  href="https://www.youtube.com/channel/UCiEkyVJS5JMzp-0DxAamcQg/videos?view=0&sort=p"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('YouTube에서 디플리 보기')}
                  className={s.socialLink}
                >
                  <IconYoutube size={28} aria-hidden="true" />
                </a>
              </div>
            </div>
          </aside>
        </>
      )}

      {headerSubComponent && headerSubComponent}
    </header>
  );
}
